import React, { createRef } from "react";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { Link, withRouter } from "react-router-dom";
import { Breadcrumb, Button, Container } from "react-bootstrap";
import { faDownload, faList } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import MyTable from "components/MyTable";
import WebsiteService from "services/WebsiteService";
import Toast from "services/Toast";
import Utils from "services/Utils";

/**
 * @component
 * @category Scenes
 * @subcategory Bucket
 */
class List extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            days: ["...", "...", "...", "...", "...", "...", "..."],
        };
        this.tableRef = createRef();
        this.downloadSpreadsheet = this.downloadSpreadsheet.bind(this);
    }

    downloadSpreadsheet() {
        this.tableRef.current.CSV();
    }

    componentDidMount() {
        WebsiteService.buckets().then((data) => {
            if (data === false) {
                Toast.error(
                    "Something went wrong while loading the list of buckets"
                );
                return;
            }
            this.setState({ days: data.trends, loading: false }, () => {
                const list = data.items.map((row) => ({
                    bucket: row.bucket,
                    engine: row.engine,
                    websites: row.websites,
                    share: row.websites_share.toFixed(2),
                    crawled_websites: row.crawled_websites,
                    share_crawled: row.crawled_websites_share.toFixed(2),
                    day0: row.trends[0],
                    day1: row.trends[1],
                    day2: row.trends[2],
                    day3: row.trends[3],
                    day4: row.trends[4],
                    day5: row.trends[5],
                    day6: row.trends[6],
                    sum:
                        row.trends[1] +
                        row.trends[2] +
                        row.trends[3] +
                        row.trends[4] +
                        row.trends[5] +
                        row.trends[6],
                }));
                this.tableRef.current?.populate(list);
            });
        });
    }

    get renderColumns() {
        return {
            name: (row) => (
                <Link
                    className="link"
                    to={`/website/${row.id}`}
                    target="_blank"
                >
                    {row.name}
                </Link>
            ),
            day0: {
                className: "gray",
                format: (row) => (row.day0 ? row.day0 : ""),
            },
            day1: (row) => Utils.trendColumn(row, "day1"),
            day2: (row) => Utils.trendColumn(row, "day2"),
            day3: (row) => Utils.trendColumn(row, "day3"),
            day4: (row) => Utils.trendColumn(row, "day4"),
            day5: (row) => Utils.trendColumn(row, "day5"),
            day6: (row) => Utils.trendColumn(row, "day6"),
            sum: (row) => Utils.trendColumn(row, "sum"),
            control: (row) => (
                <Button
                    as={Link}
                    to={`/bucket/${row.engine ?? ""}/${row.bucket ?? ""}`}
                    size="sm"
                    variant="secondary"
                    className="tdButton"
                >
                    <FontAwesomeIcon icon={faList} />
                </Button>
            ),
        };
    }

    get headers() {
        return [
            {
                field: "bucket",
                label: "Bucket",
                searchable: true,
                sortable: true,
                width: "250px",
            },
            {
                field: "engine",
                label: "Engine",
                allowEmpty: true,
                sortable: true,
                options: [
                    { label: "V1", value: "v1" },
                    { label: "NG", value: "ng" },
                ],
                width: "100px",
            },
            {
                field: "websites",
                label: "# Websites",
                hint: "Total number of websites belonging to the bucket",
                sortable: true,
                width: "105px",
            },
            {
                field: "share",
                label: "% Websites",
                sortable: true,
                hint: "% of All Websites",
                width: "105px",
            },
            {
                field: "crawled_websites",
                label: "# Crawled",
                hint: "Total number of crawled websites belonging to the bucket",
                sortable: true,
                width: "100px",
            },
            {
                field: "share_crawled",
                label: "% Crawled",
                sortable: true,
                hint: "% of All Crawled Websites",
                width: "100px",
            },
            {
                field: "day0",
                width: "80px",
                hint: "Reference date",
                label: this.state.days[0],
                sortable: true,
                searchable: true,
            },
            {
                field: "day1",
                width: "80px",
                hint: `Variation between ${this.state.days[0]} and ${this.state.days[1]}`,
                label: this.state.days[1],
                sortable: true,
                searchable: true,
            },
            {
                field: "day2",
                width: "80px",
                hint: `Variation between ${this.state.days[1]} and ${this.state.days[2]}`,
                label: this.state.days[2],
                sortable: true,
                searchable: true,
            },
            {
                field: "day3",
                width: "80px",
                hint: `Variation between ${this.state.days[2]} and ${this.state.days[3]}`,
                label: this.state.days[3],
                sortable: true,
                searchable: true,
            },
            {
                field: "day4",
                width: "80px",
                hint: `Variation between ${this.state.days[3]} and ${this.state.days[4]}`,
                label: this.state.days[4],
                sortable: true,
                searchable: true,
            },
            {
                field: "day5",
                width: "80px",
                hint: `Variation between ${this.state.days[4]} and ${this.state.days[5]}`,
                label: this.state.days[5],
                sortable: true,
                searchable: true,
            },
            {
                field: "day6",
                width: "80px",
                hint: `Variation between ${this.state.days[5]} and ${this.state.days[6]}`,
                label: this.state.days[6],
                sortable: true,
                searchable: true,
            },
            {
                field: "sum",
                label: "Sum",
                width: "80px",
                hint: "Cumulative variation over the last week",
                sortable: true,
                searchable: true,
            },
            {
                field: "control",
            },
        ];
    }

    render() {
        return (
            <Container className="pt-4">
                <span
                    style={{
                        float: "right",
                    }}
                >
                    <Button
                        disabled={this.state.loading}
                        size="sm"
                        className="tdButton"
                        variant="success"
                        label="Download CSV"
                        onClick={this.downloadSpreadsheet}
                    >
                        <FontAwesomeIcon icon={faDownload} /> Download
                    </Button>
                </span>
                <Breadcrumb className="dd">
                    <Breadcrumb.Item>Profiles</Breadcrumb.Item>
                    <Breadcrumb.Item active>Bucket</Breadcrumb.Item>
                </Breadcrumb>
                <MyTable
                    name="bucket-list"
                    ref={this.tableRef}
                    headers={this.headers}
                    renderColumns={this.renderColumns}
                    sortBy="share_crawled"
                    sortOrder="desc"
                />
            </Container>
        );
    }
}

export default withRouter(List);
